import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteConfirmationModal from "../../../components/commonComponent/DeleteConfirmationModal";
import Pagination from "../../../components/commonComponent/Pagination";
import Search from "../../../components/commonComponent/Search";
import Table from "../../../components/commonComponent/Table";
import ViewModal from "../../../components/commonComponent/ViewModal";
import useModal from "../../../hooks/useModal";
import "../../../styles/SolarStation.css";
import { deleteData, fetchData, getCurrentData } from "../../../utils/utils";
import useLoader from "../../../hooks/useLoader";
import StatusToggleButton from "../../../components/commonComponent/StatusToggleButton";

const API_URL = process.env.REACT_APP_API_URL;
const ITEMS_PER_PAGE = 10;

const Attribute = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(""); // Category filter state
  const [error, setError] = useState(null);
  const { loading, showLoader, hideLoader } = useLoader();

  const {
    showDeleteModal,
    showViewModal,
    selectedItem,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleOpenViewModal,
    handleCloseViewModal,
  } = useModal();

  useEffect(() => {
    if (searchTerm.length >= 2 || searchTerm === "") {
      fetchData(
        `${API_URL}/attribute/getAllAttribute`,
        setData,
        setError,
        setFilteredData,
        currentPage,
        searchTerm,
        showLoader,
        hideLoader
      );
    }
  }, [currentPage, searchTerm]);

  const handleSearch = (term) => {
    if (term.length >= 2) {
      setSearchTerm(term);
      setCurrentPage(1);
    } else if (term.length === 0) {
      setSearchTerm("");
      setCurrentPage(1);
    }
  };

  const handleStatusChange = async (itemId, newStatus) => {
    try {
      await fetch(`${API_URL}/attribute/updateAttributeById/${itemId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: newStatus }),
      });
      setData((prevData) =>
        prevData.map((item) =>
          item._id === itemId ? { ...item, status: newStatus } : item
        )
      );
    } catch (error) {
      toast.error("Error updating status", { autoClose: 1000 });
    }
  };

  const handleDeleteConfirm = () => {
    showLoader();
    deleteData(
      `${API_URL}/attribute/deleteAttributeById`,
      selectedItem._id,
      setError
    )
      .then(() => {
        setData((prevData) =>
          prevData.filter((item) => item._id !== selectedItem._id)
        );
        toast.success("Deleted successfully");
      })
      .catch((error) => {
        setError(error);
        toast.error("Error deleting data");
      });
    hideLoader();
    handleCloseDeleteModal();
  };

  const fieldsToShow = {
    Name: "name",
    Status: "status",
  };

  const columns = [
    { header: "Name", accessor: "name" },
    {
      header: "Status",
      accessor: "status",
      render: (item) => (
        <StatusToggleButton
          isActive={item.status}
          itemId={item._id}
          onStatusChange={handleStatusChange}
        />
      ),
    },
    {
      header: "Category", // New column for Category
      accessor: "categoryId.name", // Access category name from categoryId
      render: (item) => item.categoryId?.name || "N/A", // Display category name or "N/A" if not available
    },
  ];

  // Filter data by category
  const filteredByCategory = categoryFilter
    ? filteredData.filter(item => item.categoryId?.name === categoryFilter)
    : filteredData;

  const totalPages = Math.ceil(filteredByCategory.length / ITEMS_PER_PAGE);
  const currentData = getCurrentData(filteredByCategory, currentPage, ITEMS_PER_PAGE);

  return (
    <div>
      <h2 className="capitalize font-bold text-[24px] mb-4">Attribute List</h2>

      <div className="flex justify-between items-center shadow-md px-8 py-[10px] rounded-md bg-[#fff] text-base mb-4">
        <div className="flex items-center space-x-4">
          <Search searchFields={["Name", "Status"]} onSearch={handleSearch} />
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setCurrentPage(1); // Reset to the first page on category change
            }}
            className="border rounded-md px-4 py-2"
          >
            <option value="">All Categories</option> 
            {data.map((item) => item.categoryId?.name).filter((value, index, self) => self.indexOf(value) === index).map((category, idx) => (
              <option key={idx} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <Link
          to="/attribute/add"
          className="inline-flex items-center justify-center rounded-md border border-white px-4 py-1.5 text-center font-medium text-white bg-[#1C2434] hover:bg-opacity-90"
        >
          Add Attribute
        </Link>
      </div>

      <div className="table-container">
        <Table
          columns={columns}
          data={currentData}  // Now it uses the category-filtered data
          showIndex={true}
          onDelete={handleOpenDeleteModal}
          onUpdate={(item) => navigate(`/attribute/edit/${item._id}`)}
          onView={handleOpenViewModal}
          actions={{
            showView: true,
            showEdit: true,
            showDelete: true,
          }}
          loading={loading}
        />
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        onConfirm={handleDeleteConfirm}
      />

      <ViewModal
        show={showViewModal}
        onHide={handleCloseViewModal}
        data={selectedItem}
        fields={fieldsToShow}
      />
    </div>
  );
};

export default Attribute;
